<template>
  <app-modal v-model="isDialogOpened"
             size="md"
             :ok-disabled="isDialogOkDisabled"
             :ok-loading="increaseDataroomPlanPending"
             @cancel="closeDialog"
             @ok="increasePlan"
  >
    <template #title>
      <p class="text--large text--bold">
        {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.title') }}
      </p>
    </template>

    <template #body>
      <div>
        <!-- CurrentOffer Offer -->
        <p class="text--small text--bold text-text-primary">
          {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.currentOffer') }}
        </p>
        <v-container class="section-container bg-surface-secondary mb-6">
          <v-row align="center" justify="space-between">
            <v-col cols="auto">
              <p class="text-text-primary text--medium text--bold">
                <i18n-t keypath="subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.dataroomPackage"
                        tag="span"
                        scope="global"
                >
                  <template #limitGb>
                    {{ parseInt(subscriptionDataroomStorage.limitGb) }}
                  </template>
                </i18n-t>
              </p>
              <p class="text-text-tertiary mb-0 text--small">
                {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.subscribedOn') }} {{ dateFormat }}
              </p>
            </v-col>
            <v-col cols="auto">
              <span class="text-text-secondary text--medium text--bold">
                <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice"
                        tag="span"
                        scope="global"
                >
                  <template #price>
                    {{ subscriptionDataroomStorage.currentTotalPriceGb }}
                  </template>
                </i18n-t>
              </span>
              <span class="text-text-secondary">{{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.month') }}</span>
            </v-col>
          </v-row>
        </v-container>

        <!-- Increase your package -->
        <p class="text--small text--bold text-text-primary">
          {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.increaseForfait') }}
        </p>
        <p class="text-text-secondary mb-2 text--small">
          {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.chooseForfait') }}
        </p>

        <v-container
          v-for="(price, index) in filteredPrices"
          :key="index"
          class="section-container mb-2 clickable"
          :class="{ 'selected': selectedPrice === price }"
          @click="selectPrice(price)"
        >
          <v-row align="center" justify="space-between">
            <v-col cols="auto">
              <p class="pellet mb-0 text--medium text--bold">
                <i18n-t keypath="subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.Go"
                        tag="span"
                        scope="global"
                >
                  <template #number>
                    {{ price.storageGb }}
                  </template>
                </i18n-t>
              </p>
            </v-col>
            <v-col cols="auto">
              <span class="mb-0 text--medium text--bold text-text-secondary">
                <i18n-t keypath="project.project-settings.ProjectSettingsDataroomUpgradeDialog.displayedPrice"
                        tag="span"
                        scope="global"
                >
                  <template #price>
                    {{ price.totalPriceGb }}
                  </template>
                </i18n-t>
              </span>
              <span class="text-text-secondary">{{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.month') }}</span>
            </v-col>
          </v-row>
        </v-container>

        <!-- Contact Us -->
        <v-container class="section-container mb-4">
          <v-row align="center" justify="space-between">
            <v-col cols="auto">
              <p class="pellet mb-0 text--medium text--bold"> {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.moreStorage') }}</p>
            </v-col>
            <v-col cols="auto">
              <app-button
                type="outlined"
                variant="neutral"
                :href="emailToSupportUrl"
              >
                {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.contactUs') }}
              </app-button>
            </v-col>
          </v-row>
        </v-container>

        <!-- Acceptance -->
        <v-checkbox
          v-model="consentGiven"
          :label="$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.acceptanceLabel')"
          class="consent-checkbox"
        ></v-checkbox>
      </div>
    </template>
  </app-modal>
</template>

<script>
import { mapActions, mapState } from 'vuex'

import { ISOToDDMMYYYY } from '@/common/utils/dates'
import { toast } from '@/common/utils/toast'
import { dialogMixin } from '@/mixins/dialogMixin'
import { GET_SUBSCRIPTION_DATAROOM_STORAGE, INCREASE_DATAROOM_PLAN } from '@/store/modules/subscription/action_types'

export default {
  name: 'SubscriptionDataroomStorageManagementDialog',
  mixins: [dialogMixin],
  data () {
    return {
      selectedPrice: null,
      consentGiven: false,
    }
  },
  computed: {
    ...mapState('subscription', ['subscriptionDataroomStorage', 'increaseDataroomPlanPending']),
    dateFormat () {
      return ISOToDDMMYYYY(this.subscriptionDataroomStorage.startDate)
    },
    filteredPrices () {
      const limit = parseInt(this.subscriptionDataroomStorage.limitGb)
      return this.subscriptionDataroomStorage.prices.filter(price => price.storageGb > limit)
    },
    emailToSupportUrl () {
      const subject = encodeURIComponent(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.mailSubj'))
      const body = encodeURIComponent(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.mailBody'))
      return `mailto:support@closd.com?subject=${subject}&body=${body}`
    },
    isDialogOkDisabled() {
      return !this.consentGiven || !this.selectedPrice?.storageGb
    },
  },
  created () {
    this.getSubscriptionDataroomStorage()
  },
  methods: {
    ...mapActions('subscription', [GET_SUBSCRIPTION_DATAROOM_STORAGE, INCREASE_DATAROOM_PLAN]),
    async getSubscriptionDataroomStorage () {
      try {
        await this.GET_SUBSCRIPTION_DATAROOM_STORAGE()
      } catch (error) {
        toast.error(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.error'))
      }
    },
    selectPrice (price) {
      this.selectedPrice = price
    },
    async increasePlan () {
      if (!this.isDialogOkDisabled) return;

      try {
        const data = {}
        if (this.selectedPrice?.storageGb) {
          data.dataroomPlanLimitGb = parseFloat(this.selectedPrice.storageGb)
        }

        await this.INCREASE_DATAROOM_PLAN(data)

        if (data.dataroomPlanLimitGb) {
          this.subscriptionDataroomStorage.limitGb = data.dataroomPlanLimitGb
        }

        this.closeDialog()
        if (this.selectedPrice) {
          toast.success(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.successMessage'))
        } else {
          toast.success(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.packagModified'))
        }
      } catch (e) {
        console.error(e)
        toast.error(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.Dialog.errorMessage'))
      }
    },
  },
}
</script>

<style scoped lang="scss">
.section-container {
  border-radius: 4px;
  padding: 12px;
  border: solid 1px rgb(var(--v-theme-border-primary));
}

.selected {
  border-color: rgb(var(--v-theme-border-info-bold));
  background-color: rgb(var(--v-theme-surface-info));
  border-width: 2px;
}

.pellet {
  padding: 4px 12px;
  border-radius: 4px;
  background-color: rgb(var(--v-theme-surface-secondary));
  color: rgb(var(--v-theme-text-secondary));
}
</style>
